import { Box, Stack, TextField } from '@mui/material';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { getResidentDetails } from '../../../../api/transports';
import { FIELDS } from '../constants';
import FacilitySelector from '../Shared/FacilitySelector';
import ResidentDropdown from './ResidentDropdown';
import TransportTypeDropdown from './TransportTypeDropdown';
import { filterOptions } from './utils';

const RESIDENT_DETAIL_FIELDS_CONFIG = [
  { key: 'unit', label: 'Unit' },
  { key: 'room', label: 'Room' },
  { key: 'bed', label: 'Bed' },
];
const RESIDENT_DETAIL_FIELDS = RESIDENT_DETAIL_FIELDS_CONFIG.map((field) => field.key);
const DEFAULT_RESIDENT_DETAILS_DRAFT = Object.fromEntries(
  RESIDENT_DETAIL_FIELDS_CONFIG.map((field) => [field.key, ''])
);
const RESIDENT_FIELD_LABEL_BY_KEY = Object.fromEntries(
  RESIDENT_DETAIL_FIELDS_CONFIG.map((field) => [field.key, field.label])
);

InitialTripInfoStep.propTypes = {
  form: PropTypes.shape({
    formState: PropTypes.object.isRequired,
    formDispatch: PropTypes.func.isRequired,
  }).isRequired,
  setIsInitialTripInfoSelected: PropTypes.func,
  setPendingResidentDetailsUpdate: PropTypes.func,
  setFields: PropTypes.func.isRequired,
  residentsList: PropTypes.array,
  residentsLoading: PropTypes.bool,
};

export default function InitialTripInfoStep({
  form,
  setIsInitialTripInfoSelected,
  setPendingResidentDetailsUpdate,
  setFields,
  residentsList,
  residentsLoading,
}) {
  const { formState, formDispatch } = form;
  const transportsState = useSelector((state) => state.transports);
  const facilities = transportsState?.data?.facilities ?? [];
  const transportTypes = transportsState?.data?.transportTypes ?? [];
  const isLoading = transportsState?.isLoading ?? false;
  const isGroupTrip = transportTypes.find((t) => t.id === formState.TransportTypeId)?.name === 'Group Trip';
  const [residentInputValue, setResidentInputValue] = useState('');
  const [residentDetails, setResidentDetails] = useState(null);
  const [residentDetailsDraft, setResidentDetailsDraft] = useState(DEFAULT_RESIDENT_DETAILS_DRAFT);
  const [residentDetailsLoading, setResidentDetailsLoading] = useState(false);

  const selectedResidentId = !isGroupTrip && Array.isArray(formState.ResidentIds) ? formState.ResidentIds[0] : null;

  const selectedResidents = useMemo(() => {
    if (isGroupTrip) {
      return residentsList?.filter(
        (r) => Array.isArray(formState.ResidentIds) && formState.ResidentIds.includes(r.value)
      );
    }
    return (
      residentsList?.find((r) => Array.isArray(formState.ResidentIds) && formState.ResidentIds[0] === r.value) || null
    );
  }, [isGroupTrip, residentsList, formState.ResidentIds]);

  const handleResidentChange = (_, newValue) => {
    if (isGroupTrip) {
      setFields({ [FIELDS.RESIDENT_IDS]: Array.isArray(newValue) ? newValue.map((p) => p.value) : [] });
    } else {
      setFields({ [FIELDS.RESIDENT_IDS]: newValue ? [newValue.value] : [] });
    }
  };

  const missingResidentFieldKeys = useMemo(() => {
    if (!residentDetails) return [];
    return RESIDENT_DETAIL_FIELDS.filter((field) => residentDetails[field] == null);
  }, [residentDetails]);

  const handleResidentDetailChange = (field) => (event) => {
    const { value } = event.target;
    setResidentDetailsDraft((previous) => ({ ...previous, [field]: value }));
  };

  useEffect(() => {
    if (formState.TransportTypeId && formState.FacilityId && formState.ResidentIds?.length > 0) {
      setIsInitialTripInfoSelected(true);
    } else setIsInitialTripInfoSelected(false);
  }, [formState.TransportTypeId, formState.FacilityId, formState.ResidentIds, setIsInitialTripInfoSelected]);

  useEffect(() => {
    if (!selectedResidentId) {
      setResidentDetails(null);
      setResidentDetailsDraft(DEFAULT_RESIDENT_DETAILS_DRAFT);
      setPendingResidentDetailsUpdate(null);
      return;
    }

    setResidentDetailsLoading(true);
    getResidentDetails(selectedResidentId)
      .then((response) => {
        const details = response?.data || null;
        setResidentDetails(details);
        setResidentDetailsDraft({
          unit: details?.unit ?? '',
          room: details?.room ?? '',
          bed: details?.bed ?? '',
        });
      })
      .catch(() => {
        setResidentDetails(null);
        setResidentDetailsDraft(DEFAULT_RESIDENT_DETAILS_DRAFT);
        setPendingResidentDetailsUpdate(null);
      })
      .finally(() => {
        setResidentDetailsLoading(false);
      });
  }, [selectedResidentId, setPendingResidentDetailsUpdate]);

  useEffect(() => {
    if (!residentDetails?.id || isGroupTrip) {
      setPendingResidentDetailsUpdate(null);
      return;
    }

    const missingFields = missingResidentFieldKeys;
    if (missingFields.length === 0) {
      setPendingResidentDetailsUpdate(null);
      return;
    }

    const payload = {
      id: residentDetails.id,
      unit: (residentDetailsDraft.unit || '').trim() || null,
      room: (residentDetailsDraft.room || '').trim() || null,
      bed: (residentDetailsDraft.bed || '').trim() || null,
    };

    const hasAnyValueToSave = missingFields.some((field) => (residentDetailsDraft[field] || '').trim() !== '');
    setPendingResidentDetailsUpdate(hasAnyValueToSave ? payload : null);
  }, [residentDetails, residentDetailsDraft, isGroupTrip, missingResidentFieldKeys, setPendingResidentDetailsUpdate]);

  return (
    <Stack mt={1} spacing={3}>
      <FacilitySelector
        facilities={facilities}
        value={formState.FacilityId}
        onChange={(val) => formDispatch({ type: 'setFacilityId', payload: val })}
        isLoading={isLoading}
      />
      <TransportTypeDropdown
        transportTypes={transportTypes}
        value={formState.TransportTypeId}
        onChange={(val) => setFields({ [FIELDS.TRANSPORT_TYPE_ID]: val })}
        isLoading={isLoading}
      />
      <ResidentDropdown
        isGroupTrip={isGroupTrip}
        residents={selectedResidents}
        onChange={handleResidentChange}
        inputValue={residentInputValue}
        onInputChange={(_, v) => setResidentInputValue(v)}
        filterOptions={filterOptions}
        residentsList={residentsList}
        facilityId={formState.FacilityId}
        isLoading={residentsLoading}
      />
      {!isGroupTrip && missingResidentFieldKeys.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3, flexWrap: 'wrap', mt: 0.5 }}>
          {missingResidentFieldKeys.map((field) => (
            <Box key={field} sx={{ width: 132 }}>
              <TextField
                fullWidth
                label={RESIDENT_FIELD_LABEL_BY_KEY[field]}
                value={residentDetailsDraft[field]}
                onChange={handleResidentDetailChange(field)}
                disabled={!selectedResidentId || residentDetailsLoading}
                variant="outlined"
                inputProps={{ 'aria-label': RESIDENT_FIELD_LABEL_BY_KEY[field] }}
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: 42,
                    borderRadius: '10px',
                  },
                  '& .MuiInputLabel-root': {
                    color: '#707070',
                    fontSize: '13px',
                    lineHeight: 1,
                  },
                  '& .MuiOutlinedInput-input': {
                    px: 1.25,
                  },
                }}
              />
            </Box>
          ))}
        </Box>
      )}
    </Stack>
  );
}
